import { useRef, type KeyboardEvent, type PointerEvent } from 'react';
import { panelWidthRange, resizedPanels, type PanelWidths, type ResizeSide } from './dashboardPreferences';

interface Props { side: ResizeSide; widths: PanelWidths; containerWidth: number; onChange: (widths: PanelWidths) => void }

export default function PanelResizer({ side, widths, containerWidth, onChange }: Props) {
  const dragging = useRef<{ pointerId: number; x: number; widths: PanelWidths } | null>(null);
  const key = side === 'timing' ? 'timingWidth' : 'sidebarWidth';
  const range = panelWidthRange(widths, containerWidth, side);
  function start(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragging.current = { pointerId: event.pointerId, x: event.clientX, widths };
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    const drag = dragging.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = (event.clientX - drag.x) * (side === 'timing' ? 1 : -1);
    onChange(resizedPanels(drag.widths, containerWidth, side, drag.widths[key] + delta));
  }
  function keyboard(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 24 : 8;
    let width = widths[key];
    if (event.key === 'ArrowLeft') width += side === 'timing' ? -step : step;
    else if (event.key === 'ArrowRight') width += side === 'timing' ? step : -step;
    else if (event.key === 'Home') width = range.min;
    else if (event.key === 'End') width = range.max;
    else return;
    event.preventDefault();
    onChange(resizedPanels(widths, containerWidth, side, width));
  }
  return <div className={`panel-resizer resizer-${side}`} role="separator" tabIndex={0}
    aria-label={side === 'timing' ? '调整计时榜宽度' : '调整赛况栏宽度'} aria-orientation="vertical"
    aria-valuemin={range.min} aria-valuemax={range.max} aria-valuenow={widths[key]} aria-valuetext={`${widths[key]} 像素`}
    title="拖动调整宽度；左右方向键微调，Shift 加速，Home / End 最小或最大"
    onPointerDown={start} onPointerMove={move} onPointerUp={() => { dragging.current = null; }}
    onPointerCancel={() => { dragging.current = null; }} onLostPointerCapture={() => { dragging.current = null; }} onKeyDown={keyboard} />;
}
